import React, { useMemo, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { Shimmer } from '../animations/Shimmer';
import { LoadingPulse } from '../animations/LoadingPulse';
import { defaultScrollbarStyles } from '../../design-patterns/scrollbar';

// Export for backward compatibility - now uses the global design system
export const sharedScrollbarStyles = defaultScrollbarStyles;

const TableContainer = styled.div<{ $disableScroll?: boolean }>`
  width: 100%;
  max-height: ${({ $disableScroll }) => ($disableScroll ? 'none' : '65vh')};
  overflow-x: auto;
  overflow-y: ${({ $disableScroll }) => ($disableScroll ? 'hidden' : 'auto')};
  -webkit-overflow-scrolling: ${({ $disableScroll }) => ($disableScroll ? 'initial' : 'touch')};
  border-radius: var(--radius-xl);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  overscroll-behavior-x: contain;
  position: relative;
  ${({ $disableScroll }) => ($disableScroll ? css`
    scrollbar-width: none;
    scrollbar-color: transparent transparent;

    &::-webkit-scrollbar {
      width: 0;
      height: 0;
      display: none;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background: transparent;
    }
  ` : sharedScrollbarStyles)}

  @media (max-width: 48rem) { /* --breakpoint-tablet */
    border-radius: var(--radius-md);
    max-height: ${({ $disableScroll }) => ($disableScroll ? 'none' : '70vh')};
    overflow-x: ${({ $disableScroll }) => ($disableScroll ? 'visible' : 'scroll')};
    -webkit-overflow-scrolling: touch;
    scrollbar-width: ${({ $disableScroll }) => ($disableScroll ? 'none' : 'auto')};
  }
  
  @media (max-width: 30rem) { /* --breakpoint-mobile */
    border-radius: var(--radius-md);
    max-height: ${({ $disableScroll }) => ($disableScroll ? 'none' : '60vh')};
  }
`;

const Table = styled.table`
  width: max-content;
  min-width: 100%;
  background: transparent;
  border-collapse: collapse;
  table-layout: fixed;
  contain: layout;
  will-change: auto;
  position: relative;
  border-spacing: 0;
  
  thead {
    position: relative;
    z-index: 20; /* Ensure thead stays above tbody */
  }
  
  tbody {
    position: relative;
    z-index: 0; /* Lower z-index so body stays behind header */
  }
  
  @media (max-width: 48rem) { /* --breakpoint-tablet */
    font-size: var(--font-size-sm);
  }
  
  @media (max-width: 30rem) { /* --breakpoint-mobile */
    font-size: var(--font-size-xs);
  }
`;

const StatsTableRow = styled.tr`
  border-bottom: 1px solid var(--color-border);
  transition: background 0.25s var(--transition-smooth);

  &:nth-child(even) td {
    background: var(--color-background-alt);
  }

  &:hover td {
    background: rgba(var(--color-accent-rgb), 0.08);
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const StatsTableHeaderCell = styled.th<{ $isFixed?: boolean; $stickyLeft?: number }>`
  padding: var(--spacing-2) var(--spacing-2);
  font-size: var(--font-size-sm);
  color: var(--color-text-primary);
  font-weight: 600;
  white-space: nowrap;
  text-align: left;
  overflow: visible;
  background: var(--color-surface-alt);
  position: sticky;
  top: 0;
  border-bottom: 1px solid var(--color-border-strong);
  backdrop-filter: blur(0.75rem);
  
  @media (max-width: 48rem) { /* --breakpoint-tablet */
    padding: var(--spacing-1) var(--spacing-1);
    font-size: var(--font-size-xs);
    white-space: nowrap;
    line-height: var(--line-height-tight);
  }
  
  ${props => props.$isFixed && css`
    background: linear-gradient(135deg, var(--color-surface-alt), var(--color-surface)) !important;
    z-index: calc(var(--z-sticky) + 20);
    border-right: 1px solid var(--color-border-strong);
    min-width: 12.5rem; /* 200px in rem */
    width: 12.5rem;
    font-weight: 600;
    box-shadow: 0.375rem 0 var(--spacing-6) rgba(0, 0, 0, 0.1);
    left: ${props.$stickyLeft ?? 0}rem;
    
    /* Override hover and even row backgrounds for header */
    ${StatsTableRow}:hover & {
      background: linear-gradient(135deg, var(--color-surface-alt), var(--color-surface)) !important;
    }
    
    ${StatsTableRow}:nth-child(even) & {
      background: linear-gradient(135deg, var(--color-surface-alt), var(--color-surface)) !important;
    }
    
    ${StatsTableRow}:nth-child(even):hover & {
      background: linear-gradient(135deg, var(--color-surface-alt), var(--color-surface)) !important;
    }
    
    @media (max-width: 48rem) { /* --breakpoint-tablet */
      min-width: 9.375rem; /* 150px in rem */
      width: 9.375rem;
      font-size: var(--font-size-xs);
    }
    
    @media (max-width: 30rem) { /* --breakpoint-mobile */
      min-width: 8rem !important; /* 128px - adequate width for names */
      width: 8rem !important;
      max-width: 10rem !important; /* 160px - allows full names */
      white-space: normal !important; /* Allow wrapping */
      word-wrap: break-word;
      overflow-wrap: break-word;
      line-height: 1.3;
      font-size: var(--font-size-xs);
    }
  `}
  
  ${props => !props.$isFixed && css`
    z-index: calc(var(--z-sticky) + 10);
    min-width: 3.75rem; /* 60px in rem */
    text-align: center;
    
    @media (max-width: 48rem) { /* --breakpoint-tablet */
      min-width: 2.1875rem; /* 35px in rem */
      font-size: var(--font-size-xs);
    }
  `}
`;

const StatsTableCell = styled.td<{ $isFixed?: boolean; $stickyLeft?: number }>`
  padding: var(--spacing-2) var(--spacing-2);
  font-size: var(--font-size-sm);
  color: var(--color-text-primary);
  font-weight: 500;
  white-space: nowrap;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  background-clip: padding-box;
  border-bottom: 1px solid var(--color-border);
  transition: background 0.25s var(--transition-smooth);
  
  @media (max-width: 48rem) { /* --breakpoint-tablet */
    padding: var(--spacing-1) var(--spacing-1);
    font-size: var(--font-size-xs);
    white-space: nowrap;
    line-height: var(--line-height-tight);
  }
  
  ${props => props.$isFixed && css`
    position: sticky;
    background: linear-gradient(135deg, var(--color-surface-alt), var(--color-surface)) !important;
    z-index: calc(var(--z-sticky) + 15);
    border-right: 1px solid var(--color-border-strong);
    min-width: 12.5rem; /* 200px in rem */
    width: 12.5rem;
    font-weight: 600;
    box-shadow: 0.375rem 0 var(--spacing-6) rgba(0, 0, 0, 0.1);
    left: ${props.$stickyLeft ?? 0}rem;
    overflow: hidden;
    
    /* Override hover and even row backgrounds */
    ${StatsTableRow}:hover & {
      background: linear-gradient(135deg, var(--color-surface-alt), var(--color-surface)) !important;
    }
    
    ${StatsTableRow}:nth-child(even) & {
      background: linear-gradient(135deg, var(--color-surface-alt), var(--color-surface)) !important;
    }
    
    ${StatsTableRow}:nth-child(even):hover & {
      background: linear-gradient(135deg, var(--color-surface-alt), var(--color-surface)) !important;
    }
    
    @media (max-width: 48rem) { /* --breakpoint-tablet */
      min-width: 9.375rem; /* 150px in rem */
      width: 9.375rem;
      font-size: var(--font-size-xs);
    }
    
    @media (max-width: 30rem) { /* --breakpoint-mobile */
      min-width: 8rem !important; /* 128px - adequate width for names */
      width: 8rem !important;
      max-width: 10rem !important; /* 160px - allows full names */
      white-space: normal !important; /* Allow wrapping */
      word-wrap: break-word;
      overflow-wrap: break-word;
      line-height: 1.3;
      overflow: visible !important; /* Allow text to wrap */
      text-overflow: unset !important; /* Remove ellipsis */
      font-size: var(--font-size-xs);
    }
  `}
  
  ${props => !props.$isFixed && css`
    z-index: 1;
    min-width: 3.75rem; /* 60px in rem */
    text-align: center;
    
    @media (max-width: 48rem) { /* --breakpoint-tablet */
      min-width: 2.1875rem; /* 35px in rem */
      font-size: var(--font-size-xs);
    }
  `}
`;

const LoadingState = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-6);
  padding: var(--spacing-8);
  min-height: 13.75rem; /* 220px in rem */
  align-items: center;
  justify-content: center;
`;

const LoadingMessage = styled.div`
  text-align: center;
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
`;

const NoDataMessage = styled.div`
  text-align: center;
  padding: var(--spacing-8);
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
`;

export interface TableColumn {
  key: string;
  label: string;
  isFixed?: boolean;
  align?: 'left' | 'center' | 'right';
}

export interface TableRow {
  [key: string]: any;
}

export interface StatsTableProps {
  columns: TableColumn[];
  data: TableRow[];
  loading?: boolean;
  loadingMessage?: string;
  noDataMessage?: string;
  stickyFirstColumn?: boolean;
  stickyLeadingColumnCount?: number;
  disableContainerScroll?: boolean;
  isReady?: boolean;
}

// Utility function to calculate optimal column widths
const calculateColumnWidths = (columns: TableColumn[], data: TableRow[]): { [key: string]: number } => {
  const widths: { [key: string]: number } = {};
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 480; // 30rem = 480px
  
  columns.forEach(column => {
    let calculatedWidth = column.label.length * 8.5; // Base width for header text
    
    data.forEach(row => {
      const cellValue = String(row[column.key] || '-');
      const cellWidth = cellValue.length * 8; // More precise character width
      calculatedWidth = Math.max(calculatedWidth, cellWidth);
    });
    
    // Set minimum and maximum widths - give adequate space for names on mobile
    const minWidth = column.isFixed 
      ? (isMobile ? 128 : 260) // 128px (8rem) minimum on mobile for full names
      : (isMobile ? 60 : 140);
    const maxAllowedWidth = column.isFixed
      ? (isMobile ? 160 : 560) // 160px (10rem) max on mobile allows wrapping
      : (isMobile ? 80 : 420);
    
    widths[column.key] = Math.min(Math.max(calculatedWidth, minWidth), maxAllowedWidth);
  });
  
  return widths;
};

export const StatsTable: React.FC<StatsTableProps> = ({
  columns,
  data,
  loading = false,
  loadingMessage = 'Loading...',
  noDataMessage = 'No data available',
  stickyFirstColumn = true,
  stickyLeadingColumnCount,
  disableContainerScroll = false,
  isReady = true
}) => {
  // Suppress ResizeObserver errors for this component
  useEffect(() => {
    const originalError = console.error;
    console.error = (...args) => {
      const errorMessage = args[0]?.toString?.() || '';
      if (errorMessage.includes('ResizeObserver') || 
          errorMessage.includes('loop completed') ||
          errorMessage.includes('undelivered notifications')) {
        return;
      }
      originalError.apply(console, args);
    };

    return () => {
      console.error = originalError;
    };
  }, []);

  // Track window size for responsive column widths
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1920);
  
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculate optimal column widths based on content (recalculates on window resize)
  const columnWidths = useMemo(() => calculateColumnWidths(columns, data), [columns, data, windowWidth]);
  const isMobile = windowWidth <= 480; // 30rem = 480px
  const effectiveStickyCount = useMemo(() => {
    if (typeof stickyLeadingColumnCount === 'number') {
      return Math.max(0, stickyLeadingColumnCount);
    }
    return stickyFirstColumn ? 2 : 0;
  }, [stickyLeadingColumnCount, stickyFirstColumn]);

  const stickyOffsets = useMemo(() => {
    if (effectiveStickyCount === 0) return [];

    return columns.map((_, index) => {
      if (index >= effectiveStickyCount) return 0;

      let offset = 0;
      for (let i = 0; i < index; i += 1) {
        const previousColumnKey = columns[i].key;
        offset += columnWidths[previousColumnKey] ?? 0;
      }

      return offset;
    });
  }, [columns, columnWidths, effectiveStickyCount]);
  if (loading || !isReady) {
    return (
      <TableContainer $disableScroll={disableContainerScroll}>
        <LoadingState>
          <LoadingPulse label={loadingMessage} />
          <Shimmer
            rows={Math.min(6, Math.max(3, data.length || 4))}
            columns={Math.max(3, Math.min(columns.length || 3, 6))}
          />
          <LoadingMessage>{loadingMessage}</LoadingMessage>
        </LoadingState>
      </TableContainer>
    );
  }

  if (!data || data.length === 0) {
    return (
      <TableContainer $disableScroll={disableContainerScroll}>
        <Table>
          <tbody>
            <StatsTableRow>
              <StatsTableCell colSpan={columns.length}>
                <NoDataMessage>{noDataMessage}</NoDataMessage>
              </StatsTableCell>
            </StatsTableRow>
          </tbody>
        </Table>
      </TableContainer>
    );
  }

  const tableMarkup = (
    <Table>
      <thead>
        <StatsTableRow>
          {columns.map((column, colIndex) => (
            <StatsTableHeaderCell
              key={column.key}
              $isFixed={effectiveStickyCount > colIndex}
              $stickyLeft={stickyOffsets[colIndex]}
              style={{
                textAlign: column.align || (colIndex === 0 ? 'left' : 'right'),
                width: `${columnWidths[column.key]}px`,
                minWidth: `${columnWidths[column.key]}px`,
                ...(isMobile && effectiveStickyCount > colIndex ? {} : { maxWidth: `${columnWidths[column.key]}px` }),
              }}
            >
              {column.label}
            </StatsTableHeaderCell>
          ))}
        </StatsTableRow>
      </thead>
      <tbody>
        {data.map((row, rowIndex) => (
          <StatsTableRow key={rowIndex}>
            {columns.map((column, colIndex) => (
              <StatsTableCell
                key={column.key}
                $isFixed={effectiveStickyCount > colIndex}
                $stickyLeft={stickyOffsets[colIndex]}
                style={{
                  textAlign: column.align || (colIndex === 0 ? 'left' : 'right'),
                  width: `${columnWidths[column.key]}px`,
                  minWidth: `${columnWidths[column.key]}px`,
                  ...(isMobile && effectiveStickyCount > colIndex ? {} : { maxWidth: `${columnWidths[column.key]}px` }),
                }}
              >
                {row[column.key] === undefined || row[column.key] === null ? '-' : row[column.key]}
              </StatsTableCell>
            ))}
          </StatsTableRow>
        ))}
      </tbody>
    </Table>
  );

  if (disableContainerScroll) {
    return tableMarkup;
  }

  return <TableContainer $disableScroll={disableContainerScroll}>{tableMarkup}</TableContainer>;
};

export default StatsTable;
